import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../LangContext";
import { IBAN, SOCIALS } from "../i18n";
import {
    ShieldCheck, Users, Briefcase, Calendar, Megaphone, Heart,
    ArrowUpRight, Linkedin, Instagram, Facebook,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple mapping from icon name (stored in DB) to component
const ICONS = { ShieldCheck, Users, Briefcase, Calendar, Megaphone, Heart };

export default function Donate() {
    const { t, lang } = useLang();
    const [options, setOptions] = useState([]);

    useEffect(() => {
        axios.get(`${API}/support`).then((r) => setOptions(r.data)).catch(() => {});
    }, []);

    // Multilingual field picker with EN fallback (tolerates legacy plain strings)
    const pick = (f) => (f && typeof f === "object" ? (f[lang.toLowerCase()] || f.en || "") : f || "");

    const primary = options.find((o) => o.highlight);
    const rest = options.filter((o) => !o.highlight);

    return (
        <section id="donate" data-testid="donate-section" className="py-24 md:py-32 px-6 md:px-12 bg-inksurface relative overflow-hidden">
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-volt/10 blur-3xl rounded-full pointer-events-none" />

            <div className="relative max-w-7xl mx-auto">
                <div className="max-w-3xl mb-16">
                    <span className="text-xs font-mono uppercase tracking-[0.3em] text-volt">— {t.donate.label}</span>
                    <h2 className="font-graffiti text-4xl md:text-5xl lg:text-6xl tracking-tight mt-6 text-white leading-[0.95]">
                        {t.donate.title}
                    </h2>
                    <p className="mt-6 text-zinc-300 text-lg leading-relaxed">{t.donate.subtitle}</p>
                </div>

                {/* Primary highlight card */}
                {primary && (
                    <div
                        data-testid="support-primary"
                        className="border border-volt/40 bg-gradient-to-br from-volt/[0.08] to-transparent p-8 md:p-12 mb-6 relative overflow-hidden"
                    >
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                            <div className="lg:col-span-8">
                                <div className="flex items-center gap-3 mb-4">
                                    {renderIcon(primary.icon, "text-volt", 28)}
                                    <div className="text-xs font-mono uppercase tracking-[0.3em] text-volt">
                                        {t.donate.kbf_eyebrow}
                                    </div>
                                </div>
                                <h3 className="font-graffiti text-3xl md:text-5xl text-white leading-[1.05] tracking-tight">
                                    {pick(primary.title)}
                                </h3>
                                <p className="mt-6 text-zinc-300 text-base md:text-lg leading-relaxed">{pick(primary.description)}</p>
                            </div>
                            <div className="lg:col-span-4 flex flex-col gap-3">
                                <a
                                    href={primary.cta_link}
                                    target={primary.cta_link.startsWith("http") ? "_blank" : undefined}
                                    rel={primary.cta_link.startsWith("http") ? "noreferrer" : undefined}
                                    data-testid="donate-kbf-btn"
                                    className="btn-volt w-full px-8 py-5 text-sm inline-flex items-center justify-center gap-3 text-center"
                                >
                                    {pick(primary.cta_text)} →
                                </a>
                                <div className="mt-2 pt-4 border-t border-white/10">
                                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                                        {t.donate.iban_label}
                                    </div>
                                    <div className="mt-2 text-white font-mono text-sm">{IBAN}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Other ways to help — cards grid */}
                {rest.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
                        {rest.map((o, i) => {
                            // The "Spread the word / Follow us" card (links to #footer) shows
                            // clickable social icons instead of a CTA link.
                            const isSocial = o.cta_link === "#footer";
                            return (
                                <div
                                    key={o.id}
                                    data-testid={`support-card-${i}`}
                                    className="bg-ink group p-6 md:p-8 flex flex-col hover:bg-inksurface transition-colors"
                                >
                                    <div className="mb-5">{renderIcon(o.icon, "text-volt", 28)}</div>
                                    <h4 className="font-display font-bold uppercase text-white text-base md:text-lg tracking-tight leading-tight">
                                        {pick(o.title)}
                                    </h4>
                                    <p className="mt-4 text-sm text-zinc-400 leading-relaxed flex-1">{pick(o.description)}</p>
                                    {isSocial ? (
                                        <div className="mt-6 flex items-center gap-3">
                                            <a
                                                data-testid="support-social-linkedin"
                                                href={SOCIALS.linkedin}
                                                target="_blank"
                                                rel="noreferrer"
                                                aria-label="Urban Rookies on LinkedIn"
                                                className="w-10 h-10 border border-white/10 text-zinc-300 hover:border-volt hover:text-volt hover:-translate-y-0.5 flex items-center justify-center transition-all"
                                            >
                                                <Linkedin size={16} />
                                            </a>
                                            <a
                                                data-testid="support-social-instagram"
                                                href={SOCIALS.instagram}
                                                target="_blank"
                                                rel="noreferrer"
                                                aria-label="Urban Rookies on Instagram"
                                                className="w-10 h-10 border border-white/10 text-zinc-300 hover:border-volt hover:text-volt hover:-translate-y-0.5 flex items-center justify-center transition-all"
                                            >
                                                <Instagram size={16} />
                                            </a>
                                            <a
                                                data-testid="support-social-facebook"
                                                href={SOCIALS.facebook}
                                                target="_blank"
                                                rel="noreferrer"
                                                aria-label="Urban Rookies on Facebook"
                                                className="w-10 h-10 border border-white/10 text-zinc-300 hover:border-volt hover:text-volt hover:-translate-y-0.5 flex items-center justify-center transition-all"
                                            >
                                                <Facebook size={16} />
                                            </a>
                                        </div>
                                    ) : (
                                        <a
                                            href={o.cta_link}
                                            target={o.cta_link.startsWith("http") ? "_blank" : undefined}
                                            rel={o.cta_link.startsWith("http") ? "noreferrer" : undefined}
                                            className="mt-6 text-xs uppercase tracking-wider text-zinc-300 group-hover:text-volt inline-flex items-center gap-2 transition-colors"
                                        >
                                            {pick(o.cta_text)} <ArrowUpRight size={14} />
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
}

function renderIcon(name, cls, size) {
    const C = ICONS[name] || Heart;
    return <C size={size} className={cls} />;
}
